import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  BudgetSuggestion,
  formatMontant,
  getAverageMonthlyIncome,
  suggestBudgets,
  upsertBudgetsBatch,
} from '../lib/services/budgetsService';

interface WizardRow extends BudgetSuggestion {
  enabled: boolean;
  montant: string;
}

interface BudgetSetupWizardProps {
  isOpen: boolean;
  mois: string;
  moisLabel: string;
  onClose: () => void;
  onSaved: () => void;
}

const BudgetSetupWizard: React.FC<BudgetSetupWizardProps> = ({
  isOpen,
  mois,
  moisLabel,
  onClose,
  onSaved,
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<WizardRow[]>([]);
  const [revenuMoyen, setRevenuMoyen] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setStep(1);
      const [sug, income] = await Promise.all([suggestBudgets(10), getAverageMonthlyIncome()]);
      if (cancelled) return;
      if (sug.error) {
        setError(
          "Impossible de charger les suggestions. Vérifiez que la migration suggest_budgets est appliquée.",
        );
        setRows([]);
      } else {
        setRows(
          (sug.data || []).map((s) => ({
            ...s,
            enabled: true,
            montant: String(s.suggestion),
          })),
        );
      }
      setRevenuMoyen(income.data || 0);
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const enabledRows = useMemo(() => rows.filter((r) => r.enabled), [rows]);

  const totalBudgete = useMemo(() => {
    return enabledRows.reduce((sum, r) => {
      const n = Number(String(r.montant).replace(',', '.'));
      return sum + (Number.isFinite(n) && n > 0 ? n : 0);
    }, 0);
  }, [enabledRows]);

  const disponible = revenuMoyen - totalBudgete;
  const overIncome = revenuMoyen > 0 && totalBudgete > revenuMoyen;

  const setAll = (enabled: boolean) => {
    setRows((prev) => prev.map((r) => ({ ...r, enabled })));
  };

  const handleSave = async () => {
    const payload = enabledRows
      .map((r) => ({
        categorie_id: r.categorie_id,
        montant: Number(String(r.montant).replace(',', '.')),
      }))
      .filter((r) => Number.isFinite(r.montant) && r.montant > 0);

    if (payload.length === 0) {
      setError('Activez au moins un budget avec un montant > 0.');
      return;
    }

    setSaving(true);
    setError(null);
    const result = await upsertBudgetsBatch(mois, payload);
    setSaving(false);

    if (!result.success) {
      setError("Erreur lors de l'enregistrement des budgets.");
      return;
    }

    onSaved();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-dark-900 border border-dark-700 rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-dark-700">
          <div>
            <h2 className="text-lg font-semibold text-white">Configurer mes budgets</h2>
            <p className="text-sm text-dark-400 capitalize">Mois cible : {moisLabel}</p>
          </div>
          <button type="button" onClick={onClose} className="text-dark-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 flex gap-2 text-xs">
          {[1, 2, 3].map((n) => (
            <span
              key={n}
              className={`px-2.5 py-1 rounded-full ${
                step === n ? 'bg-primary-500/20 text-primary-300' : 'bg-dark-800 text-dark-400'
              }`}
            >
              Étape {n}
            </span>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
            </div>
          ) : error && rows.length === 0 ? (
            <p className="text-red-400 text-sm py-8 text-center">{error}</p>
          ) : step === 1 ? (
            <div className="space-y-4">
              <p className="text-sm text-dark-300">
                Basé sur vos dépenses des 3 mois précédents, avec −10&nbsp;% suggéré.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAll(true)}
                  className="px-3 py-1.5 text-sm rounded-lg bg-dark-800 text-white hover:bg-dark-700"
                >
                  Tout activer
                </button>
                <button
                  type="button"
                  onClick={() => setAll(false)}
                  className="px-3 py-1.5 text-sm rounded-lg bg-dark-800 text-white hover:bg-dark-700"
                >
                  Tout désactiver
                </button>
              </div>
              {rows.length === 0 ? (
                <p className="text-dark-400 text-sm">
                  Pas assez d&apos;historique de dépenses pour suggérer des budgets.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-dark-700">
                  <table className="w-full text-sm">
                    <thead className="bg-dark-800 text-dark-400 text-left">
                      <tr>
                        <th className="px-3 py-2">Catégorie</th>
                        <th className="px-3 py-2 text-right">Moyenne 3 mois</th>
                        <th className="px-3 py-2 text-right">Suggestion (−10 %)</th>
                        <th className="px-3 py-2 text-center">Activer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.categorie_id} className="border-t border-dark-700">
                          <td className="px-3 py-2 text-white">{row.nom}</td>
                          <td className="px-3 py-2 text-right text-dark-300">
                            {formatMontant(row.moyenne_3mois)}
                          </td>
                          <td className="px-3 py-2 text-right text-primary-300">
                            {formatMontant(row.suggestion)}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={row.enabled}
                              onChange={(e) =>
                                setRows((prev) =>
                                  prev.map((r) =>
                                    r.categorie_id === row.categorie_id
                                      ? { ...r, enabled: e.target.checked }
                                      : r,
                                  ),
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : step === 2 ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-dark-700 bg-dark-800/60 p-3 text-sm space-y-1">
                <p className="text-white">
                  Total budgété : <strong>{formatMontant(totalBudgete)}</strong>
                </p>
                <p className="text-dark-300">
                  Revenu moyen : <strong>{formatMontant(revenuMoyen)}</strong>
                </p>
                <p className={disponible >= 0 ? 'text-emerald-400' : 'text-amber-400'}>
                  Disponible : <strong>{formatMontant(disponible)}</strong>
                </p>
                {overIncome && (
                  <p className="text-amber-400 text-xs pt-1">
                    Attention : le total budgété dépasse vos revenus moyens.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                {enabledRows.map((row) => (
                  <div
                    key={row.categorie_id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-dark-700 px-3 py-2"
                  >
                    <span className="text-white text-sm truncate">{row.nom}</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={row.montant}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((r) =>
                            r.categorie_id === row.categorie_id
                              ? { ...r, montant: e.target.value }
                              : r,
                          ),
                        )
                      }
                      className="w-28 bg-dark-800 border border-dark-600 rounded-md px-2 py-1.5 text-right text-white text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-dark-300">
                {enabledRows.length} budget{enabledRows.length > 1 ? 's' : ''} seront
                enregistrés pour <span className="capitalize text-white">{moisLabel}</span>.
              </p>
              <ul className="space-y-2">
                {enabledRows.map((row) => (
                  <li
                    key={row.categorie_id}
                    className="flex justify-between text-sm border border-dark-700 rounded-lg px-3 py-2"
                  >
                    <span className="text-white">{row.nom}</span>
                    <span className="text-primary-300">
                      {formatMontant(Number(String(row.montant).replace(',', '.')) || 0)}
                    </span>
                  </li>
                ))}
              </ul>
              {error && <p className="text-red-400 text-sm">{error}</p>}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-dark-700 flex justify-between gap-3">
          <button
            type="button"
            onClick={() => (step === 1 ? onClose() : setStep((s) => s - 1))}
            className="px-4 py-2 text-dark-300 hover:text-white"
          >
            {step === 1 ? 'Annuler' : 'Précédent'}
          </button>
          {step < 3 ? (
            <button
              type="button"
              disabled={loading || (step === 1 && enabledRows.length === 0)}
              onClick={() => setStep((s) => s + 1)}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
            >
              Suivant
            </button>
          ) : (
            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
            >
              {saving ? 'Enregistrement…' : 'Enregistrer mes budgets'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BudgetSetupWizard;
